import { Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { useAppTFunction } from '@retail/app/i18n';
import { SupplierItemBinding } from '@retail/calculus/types';
import { chipColor, getHierarchyLevelLabel } from '@retail/calculus/utils';
import { HierarchyLevels } from '@retail/products/types';
import { Chip } from '@shared/components';
import React, { useMemo } from 'react';
import { StackedTextCell } from '@retail/components';

interface BaseProps {
  bindingType?: HierarchyLevels;
  active: boolean;
  bindingTargetItem?: SupplierItemBinding;
  abbreviation?: boolean;
  hideGtin?: boolean;
}

interface AdjustableSizeProps extends BaseProps {
  variant?: 'adjustable';
  width?: number;
  height?: number;
}
interface FixedSizeProps extends BaseProps {
  variant?: 'fixed';
  size: 'small';
}

type Props = AdjustableSizeProps | FixedSizeProps;

export const LevelChip = ({
  bindingType,
  active,
  bindingTargetItem,
  variant = 'adjustable',
  abbreviation,
  hideGtin,
  ...props
}: Props) => {
  const t = useAppTFunction();
  const theme = useTheme();

  const label = useMemo(
    () =>
      getHierarchyLevelLabel({
        t,
        bindingType,
        packageType: bindingTargetItem?.packageType,
        abbreviation,
      }),
    [abbreviation, bindingTargetItem?.packageType, bindingType, t]
  );

  const fullLabel = useMemo(
    () =>
      abbreviation
        ? getHierarchyLevelLabel({ t, bindingType, packageType: bindingTargetItem?.packageType })
        : '',
    [abbreviation, bindingTargetItem?.packageType, bindingType, t]
  );

  const { bg, text } = chipColor({
    palette: theme.vars.palette,
    bindingType,
    packageType: bindingTargetItem?.packageType,
    active,
  });

  return (
    <StackedTextCell
      topLine={
        <Tooltip title={fullLabel}>
          <Chip
            sx={variant === 'adjustable' ? { width: 144, height: 36 } : { padding: 0 }}
            size={variant === 'fixed' ? (props as FixedSizeProps).size : undefined}
            backgroundColor={bg}
            textColor={text}
            label={
              <Stack
                alignItems="center"
                fontFamily={abbreviation ? 'monospace' : undefined}
                fontWeight={abbreviation ? 'bold' : undefined}
              >
                {label}
              </Stack>
            }
          />
        </Tooltip>
      }
      bottomLine={
        bindingTargetItem?.gtinCode &&
        !hideGtin && (
          <Typography variant="body2" color="text.secondary" fontWeight="bold">
            {`${t('products.mgItem.supplierItem.gtin')}: ${bindingTargetItem.gtinCode}`}
          </Typography>
        )
      }
      nullableBottomLine
      centerAlign
    />
  );
};
